import { useTenderDeadline } from "../../../../hooks/useTenderDeadline";
import { useCallback, useEffect, useRef, useState } from "react";
import { Expeditor } from "../../../../services";

export const useOrderTenderState = ({ order, orderId, isOpen, isLoading, userCompanyId }) => {
  const { isExpired: isTenderExpiredByServer, isReady: isTenderTimeReady } = useTenderDeadline(order?.tenderDateEnd);
  const bidsRequestIdRef = useRef(0);
  const [bidsState, setBidsState] = useState(null);
  const status = (order?.status || "").toLowerCase();
  const currentOrderId = order?.id == null ? null : String(order.id);
  const currentCompanyId = userCompanyId == null ? null : String(userCompanyId);
  const isComplexTender = order?.isComplexTender === true;
  const keepOrderDetails =
    status === "awaitingoffer" ||
    (userCompanyId != null && order?.expeditorId != null && String(order.expeditorId) === String(userCompanyId));
  const isOtherExpeditorAssigned =
    userCompanyId != null && !!order?.expeditorId && String(order.expeditorId) !== String(userCompanyId);
  const needsBids =
    order?.isTender === true ||
    order?.isComplexTender === true ||
    status === "awaitingoffer" ||
    isOtherExpeditorAssigned ||
    status === "canceled";
  const isCurrentOrder = isOpen && currentOrderId != null && currentOrderId === String(orderId);
  const isCurrentBidsState =
    bidsState?.orderId === currentOrderId &&
    bidsState?.companyId === currentCompanyId &&
    bidsState?.isComplexTender === isComplexTender;
  const bidsLoaded = isCurrentOrder && (!needsBids || (isCurrentBidsState && bidsState.hasLoaded));
  const bidsError = isCurrentBidsState && bidsState?.status === "error";
  const previousBids = isCurrentBidsState ? bidsState.bids : [];
  const isTenderEnded = Boolean(order?.tenderDateEnd) && isTenderExpiredByServer;
  const mayBeClosedTender =
    (status === "awaitingoffer" && isTenderEnded) ||
    (!keepOrderDetails &&
      status !== "canceled" &&
      status !== "assigned" &&
      isOtherExpeditorAssigned &&
      order?.isTender === true);
  const isTenderClosed = !isLoading && bidsLoaded && mayBeClosedTender && previousBids.length === 0;
  const showBidOnly = !keepOrderDetails && !isLoading && bidsLoaded && previousBids.length > 0;
  const isCheckingTender =
    isCurrentOrder &&
    (userCompanyId == null ||
      ((!keepOrderDetails || mayBeClosedTender) && needsBids && !bidsLoaded && !bidsError));
  const showOrderContent =
    isCurrentOrder &&
    !isLoading &&
    !isCheckingTender &&
    !isTenderClosed &&
    !showBidOnly &&
    !(mayBeClosedTender && bidsError);

  const fetchBids = useCallback(async () => {
    if (!isCurrentOrder || currentCompanyId == null || !needsBids) return;
    const requestId = ++bidsRequestIdRef.current;
    const nextState = {
      orderId: currentOrderId,
      companyId: currentCompanyId,
      isComplexTender,
    };
    const isSameQuery = (state) =>
      state?.orderId === nextState.orderId &&
      state?.companyId === nextState.companyId &&
      state?.isComplexTender === nextState.isComplexTender;

    setBidsState((currentState) => {
      const canKeepLoadedBids = isSameQuery(currentState) && currentState.hasLoaded;
      return {
        ...nextState,
        bids: canKeepLoadedBids ? currentState.bids : [],
        hasLoaded: Boolean(canKeepLoadedBids),
        status: "loading",
      };
    });
    try {
      const request = isComplexTender
        ? Expeditor.getComplexOrderBidsByExpeditor
        : Expeditor.getOrderBidsByExpeditor;
      const response = await request(currentOrderId);
      if (requestId !== bidsRequestIdRef.current) return;
      if (!Array.isArray(response?.data)) throw new Error("Invalid order bids response");
      setBidsState({ ...nextState, bids: response.data, hasLoaded: true, status: "loaded" });
    } catch (error) {
      if (requestId !== bidsRequestIdRef.current) return;
      setBidsState((currentState) => {
        const canKeepLoadedBids = isSameQuery(currentState) && currentState.hasLoaded;
        return {
          ...nextState,
          bids: canKeepLoadedBids ? currentState.bids : [],
          hasLoaded: Boolean(canKeepLoadedBids),
          status: "error",
        };
      });
    }
  }, [currentCompanyId, currentOrderId, isComplexTender, isCurrentOrder, needsBids]);

  useEffect(() => {
    setBidsState(null);
    fetchBids();
    return () => {
      bidsRequestIdRef.current += 1;
    };
  }, [fetchBids]);

  return {
    previousBids,
    isTimeReady: isTenderTimeReady,
    bidsLoaded,
    fetchBids,
    isTenderClosed,
    showBidOnly,
    showOrderContent,
    showLoading: isLoading || isCheckingTender,
    showBidsError: isCurrentOrder && !isLoading && bidsError,
  };
};
